"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createClient = void 0;
const ApiError_1 = __importDefault(require("./ApiError"));
const isomorphic_fetch_1 = __importDefault(require("isomorphic-fetch"));
const query_string_1 = __importDefault(require("query-string"));
const stringifyQuery = (query) => {
    Object.entries(query).forEach(([key, value]) => {
        if (Array.isArray(value)) {
            query[key] = value.join(',');
        }
    });
};
const createClient = (client) => (realm, getToken, progress) => {
    let requestsInProgress = 0;
    const progressInc = () => progress && progress.inc();
    const progressDone = () => progress && progress.done();
    const request = async (method, url, payload, auth = true, headers, query = {}, hideProgress, abortSignal, credentials) => {
        if (url.indexOf('?') !== -1) {
            throw new Error('Use query parameter instead of having query in url');
        }
        const options = {
            headers: {
                'Content-Type': 'application/json',
                'Realm': realm,
            },
            method,
        };
        if (payload instanceof FormData) {
            options.body = payload;
            delete options.headers['Content-Type'];
        }
        else if (payload) {
            options.body = JSON.stringify(payload);
        }
        if (auth) {
            options.headers.Authorization = getToken();
        }
        if (headers) {
            options.headers = Object.assign(Object.assign({}, options.headers), headers);
        }
        if (abortSignal) {
            options.signal = abortSignal;
        }
        if (credentials) {
            options.credentials = credentials;
        }
        if (query) {
            stringifyQuery(query);
        }
        try {
            if (!hideProgress) {
                requestsInProgress++;
                progressInc();
            }
            const response = await client(`${url}?${query_string_1.default.stringify(query)}`, options);
            if ((response.status <= 300 && response.statusText === 'No Content') || response.status === 204) {
                return { status: response.status };
            }
            // Check for errors BEFORE handling special content types
            if (response.status >= 300) {
                const json = await response.json();
                const { error, message } = json;
                throw new ApiError_1.default(`${error} - ${message}`, response.status, json);
            }
            // Handle binary blob responses (only for successful responses)
            const contentType = response.headers.get('Content-Type') || '';
            if (contentType.includes('application/pdf')
                || contentType.includes('application/vnd.openxmlformats-officedocument')) {
                const blob = await response.blob();
                return { data: blob, meta: {} };
            }
            // Handle JSON responses
            const json = await response.json();
            const { data, meta } = json;
            return { data, meta };
        }
        finally {
            if (!hideProgress) {
                requestsInProgress--;
                if (requestsInProgress) {
                    progressInc();
                }
                else {
                    progressDone();
                }
            }
        }
    };
    const streamRequest = async (method, url, payload, auth = true, headers, query = {}, abortSignal) => {
        if (url.indexOf('?') !== -1) {
            throw new Error('Use query parameter instead of having query in url');
        }
        const options = {
            headers: {
                'Content-Type': 'application/json',
                'Realm': realm,
                'Accept': 'text/event-stream',
            },
            method,
        };
        if (payload instanceof FormData) {
            options.body = payload;
            delete options.headers['Content-Type'];
        }
        else if (payload) {
            options.body = JSON.stringify(payload);
        }
        if (auth) {
            options.headers.Authorization = getToken();
        }
        if (headers) {
            options.headers = Object.assign(Object.assign({}, options.headers), headers);
        }
        if (abortSignal) {
            options.signal = abortSignal;
        }
        if (query) {
            stringifyQuery(query);
        }
        const response = await client(`${url}?${query_string_1.default.stringify(query)}`, options);
        if (!response.ok || !response.body) {
            const json = await response.json();
            const { error, message } = json;
            throw new ApiError_1.default(`${error} - ${message}`, response.status, json);
        }
        return response.body;
    };
    return {
        delete: async (url, options = {}) => request('delete', url, options.payload, options.auth, options.headers, options.query, options.hideProgress, options.abortSignal, options.credentials),
        get: async (url, options = {}) => request('get', url, undefined, options.auth, options.headers, options.query, options.hideProgress, options.abortSignal, options.credentials),
        put: async (url, options = {}) => request('put', url, options.payload, options.auth, options.headers, options.query, options.hideProgress, options.abortSignal, options.credentials),
        post: async (url, options = {}) => request('post', url, options.payload, options.auth, options.headers, options.query, options.hideProgress, options.abortSignal, options.credentials),
        stream: async (url, options = {}) => streamRequest('post', url, options.payload, options.auth, options.headers, options.query, options.abortSignal)
    };
};
exports.createClient = createClient;
exports.default = (0, exports.createClient)(isomorphic_fetch_1.default);
